import React, { useMemo, useState } from "react";
import { FaChevronDown, FaFilter } from "react-icons/fa";
import { guestRangeOptions } from "../../utils/banquetFilters.js";
import { mappedPincodes } from "../../utils/pincodeCoordinates.js";

const checkboxFields = [
  ["parkingAvailable", "Parking"],
  ["roomsAvailable", "Rooms"],
  ["acHall", "AC Hall"],
  ["djAllowed", "DJ"],
  ["lateNightAllowed", "Late Night"],
  ["byobAllowed", "BYOB"],
  ["valetParking", "Valet Parking"],
  ["bridalRoom", "Bridal Room"],
  ["changingRoom", "Changing Room"],
  ["powerBackup", "Power Backup"],
  ["lift", "Lift"],
  ["wheelchairFriendly", "Wheelchair Friendly"],
];

const selectClassName =
  "min-h-[46px] w-full rounded-2xl border border-emerald-100 bg-white/95 px-3.5 py-2.5 text-sm font-medium text-slate-800 shadow-sm outline-none transition hover:border-emerald-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100";
const inputClassName =
  "min-h-[46px] w-full rounded-2xl border border-emerald-100 bg-white/95 px-3.5 py-2.5 text-sm font-medium text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-emerald-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100";
const chipClassName = (active) =>
  `min-h-[44px] rounded-full border px-3.5 py-2 text-sm font-bold shadow-sm transition ${
    active
      ? "border-emerald-700 bg-gradient-to-r from-emerald-700 to-emerald-600 text-white shadow-[0_12px_24px_-14px_rgba(4,120,87,0.85)] ring-2 ring-amber-200/80"
      : "border-emerald-100 bg-white text-slate-700 hover:border-amber-200 hover:bg-amber-50/70 hover:text-emerald-900"
  }`;

const venueTypeOptions = [
  "Banquet Hall",
  "Hotel Banquet",
  "Farmhouse",
  "Lawn",
  "Rooftop",
  "Restaurant Party Hall",
  "Party Hall",
  "Poolside Venue",
];

const eventTypeOptions = [
  "Birthday",
  "First Birthday",
  "Baby Shower",
  "Welcome Baby",
  "Anniversary",
  "Wedding",
  "Engagement",
  "Ring Ceremony",
  "Corporate Event",
];

const eventSlotOptions = [
  ["fullday", "Full Day"],
  ["lunch", "Lunch"],
  ["dinner", "Dinner"],
];

const capacityOptions = ["50+", "100+", "150+", "250+", "400+", "500+"];
const parkingCapacityOptions = ["25", "50", "75", "100"];
const availabilityOptions = [
  ["today", "Available Today"],
  ["weekend", "Available This Weekend"],
  ["lunch", "Lunch Available"],
  ["dinner", "Dinner Available"],
];

export default function BanquetFilters({
  filters,
  cities,
  localities,
  quickChips,
  activeQuickChips,
  onFilterChange,
  onToggleQuickChip,
  onReset,
  mobileOpen,
  setMobileOpen,
  resolvedPincode,
}) {
  const [secondaryOpen, setSecondaryOpen] = useState(false);
  const [openSections, setOpenSections] = useState({
    searchArea: false,
    foodVenue: false,
    facilities: false,
    venueEvents: false,
    capacity: false,
    availability: false,
  });

  const guestOptions = guestRangeOptions;
  const sortOptions = useMemo(
    () => [
      { value: "", label: resolvedPincode ? "Nearest First" : "Featured / Best Rated" },
      { value: "nearest", label: "Nearest First" },
      { value: "lowest-price", label: "Lowest Price First" },
      { value: "highest-capacity", label: "Highest Capacity First" },
      { value: "best-rated", label: "Best Rated" },
      { value: "most-reviewed", label: "Most Reviewed" },
    ],
    [resolvedPincode]
  );

  const toggleSection = (key) => setOpenSections((current) => ({ ...current, [key]: !current[key] }));
  const toggleMultiSelect = (key, value) => {
    const currentValues = Array.isArray(filters[key]) ? filters[key] : [];
    onFilterChange(
      key,
      currentValues.includes(value) ? currentValues.filter((item) => item !== value) : [...currentValues, value]
    );
  };

  const renderChipGroup = (items, activeValue, onSelect, getLabel = (item) => item.label || item) => (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => {
        const value = item.value || item;
        const active = Array.isArray(activeValue) ? activeValue.includes(value) : activeValue === value;
        return (
          <button key={value} type="button" onClick={() => onSelect(value, active)} className={chipClassName(active)}>
            {getLabel(item)}
          </button>
        );
      })}
    </div>
  );

  const renderImportantFilters = ({ compact = false } = {}) => (
    <div className={compact ? "space-y-3" : "rounded-[22px] border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-amber-50/80 p-3.5 shadow-inner"}>
      {!compact ? (
        <div className="mb-3">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">Most used filters</p>
          <h3 className="mt-1 text-base font-bold text-slate-950">Find the right banquet faster</h3>
        </div>
      ) : null}

      <div className="grid gap-3">
        <div>
          <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">Area / Locality</label>
          <input
            className={inputClassName}
            type="search"
            value={filters.area}
            onChange={(event) => onFilterChange("area", event.target.value)}
            placeholder="Search by area, locality or city"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">Choose Your Date</label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <input
              className={inputClassName}
              type="date"
              min={new Date().toISOString().split("T")[0]}
              value={filters.eventDate}
              onChange={(event) => onFilterChange("eventDate", event.target.value)}
            />
            <select
              className={selectClassName}
              value={filters.eventSlot || "fullday"}
              onChange={(event) => onFilterChange("eventSlot", event.target.value)}
            >
              {eventSlotOptions.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          {filters.eventDate ? (
            <p className="mt-1.5 text-xs font-semibold text-emerald-700">Showing venues free on this date &amp; slot</p>
          ) : null}
        </div>

        <div>
          <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700">Minimum Guests</label>
          {renderChipGroup(guestOptions, filters.minimumGuestRange, (value, active) => onFilterChange("minimumGuestRange", active ? "" : value))}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-1">
          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">Pincode</label>
            <input
              className={inputClassName}
              type="text"
              inputMode="numeric"
              maxLength="6"
              value={filters.pincode}
              onChange={(event) => onFilterChange("pincode", event.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Optional pincode"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">Distance / Nearby</label>
            <select className={selectClassName} value={filters.distanceRadius} onChange={(event) => onFilterChange("distanceRadius", event.target.value)}>
              <option value="2">2 km</option>
              <option value="5">5 km</option>
              <option value="10">10 km</option>
              <option value="15">15 km</option>
              <option value="20">20 km</option>
            </select>
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">Veg / Non-Veg Food</label>
          <select className={selectClassName} value={filters.foodType} onChange={(event) => onFilterChange("foodType", event.target.value)}>
            <option value="">Any Food</option>
            <option value="veg-only">Veg Only</option>
            <option value="non-veg">Non-Veg Available</option>
            <option value="veg-non-veg">Veg + Non-Veg</option>
          </select>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-1">
          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">Alcohol</label>
            <select className={selectClassName} value={filters.alcohol} onChange={(event) => onFilterChange("alcohol", event.target.value)}>
              <option value="">Any</option>
              <option value="allowed">Alcohol Allowed</option>
              <option value="not-allowed">Alcohol Not Allowed</option>
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">Parking Available</label>
            <select className={selectClassName} value={filters.parkingStatus} onChange={(event) => onFilterChange("parkingStatus", event.target.value)}>
              <option value="">Any</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const CollapsibleSection = ({ sectionKey, title, children }) => (
    <div className="overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => toggleSection(sectionKey)}
        className="flex min-h-[46px] w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-emerald-50/70"
      >
        <span className="text-sm font-bold text-slate-900">{title}</span>
        <FaChevronDown className={`text-xs text-emerald-700 transition ${openSections[sectionKey] ? "rotate-180" : ""}`} />
      </button>
      {openSections[sectionKey] ? <div className="border-t border-emerald-100 bg-gradient-to-br from-white to-emerald-50/40 px-4 py-4">{children}</div> : null}
    </div>
  );

  const renderMoreFilters = () =>
    secondaryOpen ? (
      <div className="mt-4 space-y-4">
        <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-white to-amber-50/70 p-4 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900">Price Range</h3>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input className={inputClassName} type="number" min="0" placeholder="Min veg" value={filters.vegPriceMin} onChange={(event) => onFilterChange("vegPriceMin", event.target.value)} />
            <input className={inputClassName} type="number" min="0" placeholder="Max veg" value={filters.vegPriceMax} onChange={(event) => onFilterChange("vegPriceMax", event.target.value)} />
            <input className={inputClassName} type="number" min="0" placeholder="Min non veg" value={filters.nonVegPriceMin} onChange={(event) => onFilterChange("nonVegPriceMin", event.target.value)} />
            <input className={inputClassName} type="number" min="0" placeholder="Max non veg" value={filters.nonVegPriceMax} onChange={(event) => onFilterChange("nonVegPriceMax", event.target.value)} />
          </div>
        </div>

        <CollapsibleSection sectionKey="searchArea" title="Search & Area">
          <div className="space-y-3">
            <input className={inputClassName} type="search" value={filters.name} onChange={(event) => onFilterChange("name", event.target.value)} placeholder="Search banquet name" />
            <input className={inputClassName} type="search" value={filters.area} onChange={(event) => onFilterChange("area", event.target.value)} placeholder="Search area or locality" />
            <select className={selectClassName} value={filters.city} onChange={(event) => onFilterChange("city", event.target.value)}>
              <option value="">All cities</option>
              {cities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
            <select className={selectClassName} value={filters.locality} onChange={(event) => onFilterChange("locality", event.target.value)}>
              <option value="">All localities</option>
              {localities.map((locality) => (
                <option key={locality} value={locality}>
                  {locality}
                </option>
              ))}
            </select>
          </div>
        </CollapsibleSection>

        <CollapsibleSection sectionKey="foodVenue" title="Food & Venue">
          <div className="space-y-3">
            <select className={selectClassName} value={filters.venueStyle} onChange={(event) => onFilterChange("venueStyle", event.target.value)}>
              <option value="">Lawn / Indoor / Rooftop</option>
              <option value="indoor">Indoor</option>
              <option value="lawn">Lawn</option>
              <option value="rooftop">Rooftop</option>
              <option value="combo">Banquet + lawn</option>
            </select>
            <select className={selectClassName} value={filters.decorPolicy} onChange={(event) => onFilterChange("decorPolicy", event.target.value)}>
              <option value="">Decor policy</option>
              <option value="outside-allowed">Decor allowed</option>
              <option value="in-house-only">In-house decor only</option>
            </select>
            <select className={selectClassName} value={filters.cateringPolicy} onChange={(event) => onFilterChange("cateringPolicy", event.target.value)}>
              <option value="">Catering policy</option>
              <option value="outside-allowed">Outside catering allowed</option>
              <option value="in-house-only">In-house catering only</option>
            </select>
            <select className={selectClassName} value={filters.eventType} onChange={(event) => onFilterChange("eventType", event.target.value)}>
              <option value="">Event type</option>
              <option value="birthday">Birthday</option>
              <option value="anniversary">Anniversary</option>
              <option value="baby shower">Baby shower</option>
              <option value="engagement">Engagement</option>
              <option value="wedding">Wedding</option>
              <option value="corporate">Corporate</option>
            </select>
          </div>
        </CollapsibleSection>

        <CollapsibleSection sectionKey="venueEvents" title="Venue & Event Type">
          <div className="space-y-4">
            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-900">Venue Type</h3>
              {renderChipGroup(venueTypeOptions, filters.venueTypes, (value) => toggleMultiSelect("venueTypes", value))}
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-900">Event Type</h3>
              {renderChipGroup(eventTypeOptions, filters.eventTypes, (value) => toggleMultiSelect("eventTypes", value))}
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection sectionKey="capacity" title="Capacity, Parking & Budget">
          <div className="space-y-4">
            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-900">Seating / Floating Capacity</h3>
              {renderChipGroup(capacityOptions, filters.capacityTier, (value, active) => onFilterChange("capacityTier", active ? "" : value))}
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Parking Capacity</label>
              <select className={selectClassName} value={filters.parkingCapacity} onChange={(event) => onFilterChange("parkingCapacity", event.target.value)}>
                <option value="">Any</option>
                {parkingCapacityOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}+
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 shadow-sm">
              <input
                type="checkbox"
                checked={filters.valetParking}
                onChange={(event) => onFilterChange("valetParking", event.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span>Valet Parking Available</span>
            </label>
            <div>
              <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">Price Filter</label>
              <select className={selectClassName} value={filters.priceTier} onChange={(event) => onFilterChange("priceTier", event.target.value)}>
                <option value="">Any Budget</option>
                <option value="under-1000">Under Rs.1000 / plate</option>
                <option value="1000-1500">Rs.1000 - Rs.1500 / plate</option>
                <option value="1500-2000">Rs.1500 - Rs.2000 / plate</option>
                <option value="2000-plus">Rs.2000+</option>
                <option value="hall-rental">Hall Rental Available</option>
              </select>
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection sectionKey="availability" title="Availability">
          {renderChipGroup(
            availabilityOptions.map(([value, label]) => ({ value, label })),
            filters.availability,
            (value, active) => onFilterChange("availability", active ? "" : value)
          )}
        </CollapsibleSection>

        <CollapsibleSection sectionKey="facilities" title="Facility Filters">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-1">
            {checkboxFields.map(([key, label]) => (
              <label key={key} className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-amber-200 hover:bg-amber-50/60">
                <input
                  type="checkbox"
                  checked={filters[key]}
                  onChange={(event) => onFilterChange(key, event.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </CollapsibleSection>
      </div>
    ) : null;

  const renderPrimaryFilters = () => (
    <div className="rounded-[28px] border border-emerald-100/80 bg-white p-4 shadow-[0_24px_60px_-38px_rgba(15,23,42,0.55)] ring-1 ring-amber-100/80">
      <div className="mb-4">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">Find Venues</p>
        <h2 className="mt-1 text-xl font-bold text-slate-950">Filter banquets</h2>
      </div>

      <div className="grid gap-3">
        {renderImportantFilters()}

        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() => setSecondaryOpen((current) => !current)}
            className="inline-flex flex-1 items-center justify-center rounded-2xl border border-emerald-200 bg-white px-4 py-2.5 text-sm font-bold text-emerald-800 shadow-sm transition hover:border-amber-300 hover:bg-amber-50"
          >
            {secondaryOpen ? "Hide filters" : "More filters"}
          </button>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm transition hover:border-emerald-200 hover:bg-emerald-50"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="mt-3 flex min-w-0 flex-wrap items-center gap-2 text-xs text-slate-600">
        <span className="min-w-0 break-words font-medium text-slate-700">
          {resolvedPincode ? `Searching near ${resolvedPincode.locality}, ${resolvedPincode.city}` : `Mapped pincodes: ${mappedPincodes.join(", ")}`}
        </span>
        {resolvedPincode ? (
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-bold text-emerald-700 ring-1 ring-emerald-100">
            Within {filters.distanceRadius} km
          </span>
        ) : null}
      </div>

      {renderMoreFilters()}
    </div>
  );

  const renderQuickChips = () => (
    <div className="rounded-[28px] border border-amber-100/80 bg-gradient-to-br from-white via-white to-amber-50/70 p-4 shadow-[0_20px_50px_-38px_rgba(15,23,42,0.5)] ring-1 ring-white">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">Quick Filters</p>
        <p className="mt-1 text-sm font-medium text-slate-600">Fast shortcuts for common banquet searches.</p>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {quickChips.map((chip) => {
          const active = activeQuickChips.includes(chip);
          return (
            <button
              key={chip}
              type="button"
              onClick={() => onToggleQuickChip(chip)}
              className={`min-h-[44px] max-w-full rounded-full border px-4 py-2 text-sm font-bold shadow-sm transition ${
                active
                  ? "border-emerald-700 bg-gradient-to-r from-emerald-700 to-emerald-600 text-white shadow-[0_12px_24px_-14px_rgba(4,120,87,0.85)] ring-2 ring-amber-200/80"
                  : "border-emerald-100 bg-white text-slate-700 hover:border-amber-200 hover:bg-amber-50 hover:text-emerald-900"
              }`}
            >
              {chip}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderContent = () => (
    <div className="space-y-4">
      {renderPrimaryFilters()}
      <div className="rounded-[24px] border border-emerald-100 bg-white p-4 shadow-[0_14px_35px_-28px_rgba(15,23,42,0.35)] ring-1 ring-amber-100/70 xl:hidden">
        <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">Sort</label>
        <select className={selectClassName} value={filters.sortBy} onChange={(event) => onFilterChange("sortBy", event.target.value)}>
          {sortOptions.map((option) => (
            <option key={option.label} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {renderQuickChips()}
    </div>
  );

  return (
    <>
      <div className="hidden xl:block">{renderContent()}</div>

      <div className="space-y-3 xl:hidden">
        <div className="rounded-[24px] border border-emerald-100 bg-white p-3 shadow-[0_18px_42px_-30px_rgba(15,23,42,0.45)] ring-1 ring-amber-100/70">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">Quick Filters</p>
              <h2 className="text-base font-bold text-slate-900">Top banquet filters</h2>
            </div>
            <button
              type="button"
              onClick={onReset}
              className="min-h-[44px] rounded-full border border-emerald-100 bg-white px-3 py-2 text-xs font-bold text-emerald-800 shadow-sm"
            >
              Clear All
            </button>
          </div>
          {renderImportantFilters({ compact: true })}
        </div>
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-emerald-700 to-emerald-600 px-4 py-3 text-sm font-bold text-white shadow-[0_16px_30px_-18px_rgba(4,120,87,0.9)] ring-2 ring-amber-200/80"
        >
          <FaFilter />
          All Filters
        </button>
      </div>

      {mobileOpen ? (
        <div className="fixed inset-0 z-[70] bg-slate-950/50 backdrop-blur-[2px] xl:hidden">
          <div className="absolute inset-x-0 bottom-0 max-h-[90vh] overflow-y-auto rounded-t-[28px] bg-[#f5f7f4] p-3 shadow-[0_-20px_60px_-30px_rgba(15,23,42,0.8)] sm:p-4">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Filter Banquets</h2>
              <button type="button" onClick={() => setMobileOpen(false)} className="min-h-[44px] rounded-full bg-white px-4 py-2 text-sm font-bold text-emerald-800 shadow-sm ring-1 ring-emerald-100">
                Close
              </button>
            </div>
            {renderContent()}
          </div>
        </div>
      ) : null}
    </>
  );
}
